import { Restaurant } from "api-types";
import { StyledButton } from "igfd-ui";

import React from "react";
import { useSelector } from "react-redux";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { Button, Stack, Tooltip, Typography } from "@mui/material/";
import { styled } from "@mui/material/styles";

import { MonoChip } from "igfd-ui/src/admin";

import { ICON_SIZE } from "@dashboard/components/common/StreamlineIcon";
import { DropdownIcon, VerifiedIcon } from "@dashboard/components/common/icons";
import { GuestMenuPreviewDialog } from "@dashboard/components/guest-menu-preview/GuestMenuPreviewDialog";
import { trackGuestMenuPreviewed } from "@dashboard/components/guest-menu-preview/guestMenuEvents";
import { ReferralDialog } from "@dashboard/components/hubspot-form/ReferralDialog";
import { useDashboardAppFeatureFlag } from "@dashboard/hooks/useFeature";
import { useGuestMenuVersion } from "@dashboard/hooks/useGuestMenuVersion";
import type { ReduxState } from "@dashboard/types";
import { isAdminShellPath } from "@dashboard/utils/isAdminShellPath";

import { RestaurantModal } from "../../RestaurantModal/RestaurantModal";

export const SelectedRestaurant = ({
  isLargeScreen,
  selectedRestaurant,
}: {
  isLargeScreen: boolean;
  selectedRestaurant: Restaurant;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { menuUrlsV2, initialMenuUrlsV2Loading } = useSelector(
    (state: ReduxState) => state.menuUrlV2
  );
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );
  const showIds = useDashboardAppFeatureFlag("showIds");

  const guestMenuVersion = useGuestMenuVersion();
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const handlePreviewClick = () => {
    if (menuUrlsV2.length > 0) {
      trackGuestMenuPreviewed("header");
      setPreviewOpen(true);
    } else {
      navigate("/menu-urls");
    }
  };

  const [restaurantSelectionModal, setRestaurantSelectionModal] =
    React.useState(false);
  const [referOpen, setReferOpen] = React.useState(false);
  const openReferral = React.useCallback(() => {
    setReferOpen(true);
  }, []);

  return (
    <>
      <Stack
        display={"flex"}
        height={"36px"}
        spacing={1}
        direction={"row"}
        alignItems={"center"}
        sx={(theme) => ({
          borderRadius: "32px",
          border: `1px solid ${theme.palette.grey[400]}`,
          padding: `${theme.spacing(0)} ${theme.spacing(1)}`,
        })}
      >
        <Tooltip title="Switch restaurant" arrow>
          <DropdownIcon
            size={ICON_SIZE.small}
            sx={{ cursor: "pointer", color: "grey.700" }}
            onClick={() => setRestaurantSelectionModal(true)}
          />
        </Tooltip>
        <Tooltip title={selectedRestaurant.name} arrow>
          <Typography
            variant={"caption"}
            overflow={"hidden"}
            noWrap
            sx={(theme) => ({
              fontFamily: theme.typography.h3.fontFamily,
              fontSize: theme.typography.caption.fontSize,
              maxWidth: isLargeScreen ? "100%" : 100,
              cursor: "pointer",
            })}
            onClick={() => setRestaurantSelectionModal(true)}
          >
            {selectedRestaurant.name}
          </Typography>
        </Tooltip>
        {selectedRestaurant.verified && (
          <Tooltip title={"Verified"} arrow>
            <VerifiedIcon
              size={16}
              sx={({ palette }) => ({
                color: palette.oscar.light.icon.link,
              })}
            />
          </Tooltip>
        )}
        {userIsAdmin && showIds && (
          <MonoChip
            value={selectedRestaurant.uuid}
            display={selectedRestaurant.uuid.slice(0, 8)}
            withBackground
            withIcon
          />
        )}

        {!isAdminShellPath(location.pathname) && (
          <StyledButton
            onClick={handlePreviewClick}
            variant={"outlined"}
            color={"secondary"}
            label={"Preview"}
            size={"small"}
            loading={initialMenuUrlsV2Loading}
            sx={(theme) => ({
              color: theme.palette.secondary.main,
              fontSize: theme.typography.caption.fontSize,
              borderColor: theme.palette.secondary.light,
              borderWidth: "2px",
              height: "24px",
              lineHeight: 1,
            })}
          />
        )}
        <GuestMenuPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          initialShown={guestMenuVersion}
          // On Themes, preview the theme being edited.
          themeId={
            matchPath("/themes/:themeId", location.pathname)?.params.themeId
          }
        />
        <RestaurantModal
          open={restaurantSelectionModal}
          onClose={() => setRestaurantSelectionModal(false)}
          onNewRestaurantCreated={() => setRestaurantSelectionModal(false)}
        />
      </Stack>
      <Stack direction="row" spacing={1}>
        <StyledButton
          onClick={openReferral}
          variant={"text"}
          color={"success"}
          label={"Earn Support Credits"}
          size={"medium"}
          loading={false}
          sx={(theme) => ({
            color: theme.palette.oscar.light.content.content1,
            fontSize: theme.typography.labelSMedium?.fontSize,
            borderColor: theme.palette.oscar.light.content.content3,
            borderWidth: "1px",
            height: "24px",
            lineHeight: 1,
          })}
        />
      </Stack>
      <ReferralDialog open={referOpen} onClose={() => setReferOpen(false)} />
    </>
  );
};

const RewardPillButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: "none",
  height: 24,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  borderWidth: 1,
  borderColor: theme.palette.secondary.light,
  "&:hover": {
    borderColor: theme.palette.secondary.main,
  },
}));
